<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="openMenu">
            <ion-icon :icon="menuOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Настройки</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background" :fullscreen="true" slot="fixed" :scroll-y="false">
      <ion-item>
        <ion-label>Меню справа</ion-label>
        <ion-toggle @ionChange="menuButtonChange($event)" :checked="menuButton.position === 'right'"></ion-toggle>
      </ion-item>
      <ion-item>
        <ion-label>Кэш</ion-label>
        <ion-label slot="end" v-show="!removingCache">{{
          bufferSizeHuman
        }}</ion-label>
        <ion-icon :icon="trashOutline" v-show="bufferSize > 0 && !removingCache" @click="clearCacheExceptPlaylists('/')"
          slot="end"></ion-icon>
        <ion-spinner name="bubbles" slot="end" v-show="removingCache"></ion-spinner>
      </ion-item>
      <ion-item>
        <ion-label>Память устройства</ion-label>
        <ion-label slot="end">{{
          `${deviceDiskFreeHuman} из ${deviceDiskTotalHuman}`
        }}</ion-label>
      </ion-item>
      <!-- <ion-item>
        <ion-label>Синхронизировать видео и гайд</ion-label>
        <ion-toggle
          @ionChange="syncVideoGuideChange($event)"
          :checked="globalContext.syncVideoGuide"
        ></ion-toggle>
      </ion-item> -->
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import { MenuButton } from "@/model/menubutton.model";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import { Device } from "@capacitor/device";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToggle,
  IonToolbar,
  menuController,
} from "@ionic/vue";
import { menuOutline, trashOutline } from "ionicons/icons";
import { defineComponent, inject } from "vue";

export default defineComponent({
  name: "Settings",
  components: {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToggle,
    IonToolbar,
  },
  setup() {
    const menuButton = inject("menuButton") as MenuButton;
    const filesystemCustomUtils = new FilesystemCustomUtils();
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;

    Preferences.get({ key: "menuButtonRight" }).then((res) => {
      if (res.value) {
        menuButton.position = res.value;
      }
    });

    const openMenu = () => menuController.open("mainMenu");

    return {
      menuButton,
      trashOutline,
      menuOutline,
      openMenu,
      filesystemCustomUtils,
      isMobile,
      globalContext,
    };
  },

  data() {
    return {
      bufferSize: 0,
      removingCache: false,
      deviceDiskTotal: 0,
      deviceDiskFree: 0,
    };
  },

  computed: {
    bufferSizeHuman(): string {
      return this.getBytesHuman(this.bufferSize);
    },
    deviceDiskTotalHuman(): string {
      return this.getBytesHuman(this.deviceDiskTotal);
    },
    deviceDiskFreeHuman(): string {
      return this.getBytesHuman(this.deviceDiskFree);
    },
  },

  ionViewWillEnter() {
    this.bufferSize = 0;
    this.deviceDiskTotal = 0;
    this.deviceDiskFree = 0;
    this.countCache("/");

    if (this.isMobile) {
      Device.getInfo().then((deviceInfo) => {
        this.deviceDiskTotal = deviceInfo.realDiskTotal || 0;
        this.deviceDiskFree = deviceInfo.realDiskFree || 0;
      });
    }
  },

  methods: {
    getBytesHuman(bytes: number): string {
      let bytesH = bytes + " B";
      if (bytes > 1000 && bytes < 1000000) {
        bytesH = Math.round(bytes / 1024) + " KB";
      } else if (bytes > 1000000 && bytes < 1000000000) {
        bytesH = Math.round(bytes / 1024 / 1024) + " MB";
      } else if (bytes > 1000000000) {
        bytesH = Math.round(bytes / 1024 / 1024 / 1024) + " GB";
      }
      return bytesH;
    },

    async menuButtonChange(event: any) {
      this.menuButton.position = event.detail.checked ? "right" : "left";

      await Preferences.set({
        key: "menuButtonRight",
        value: this.menuButton.position,
      });

      await menuController.enable(false, "mainMenu");
      await menuController.enable(true, "mainMenu");
    },

    async musicBetaChange(event: any) {
      await Preferences.set({
        key: "musicBeta",
        value: event.detail.checked ? "1" : "0",
      });

      this.globalContext.musicBeta = event.detail.checked;
    },

    async videoBetaChange(event: any) {
      await Preferences.set({
        key: "videoBeta",
        value: event.detail.checked ? "1" : "0",
      });

      this.globalContext.videoBeta = event.detail.checked;
    },

    async syncVideoGuideChange(event: any) {
      await Preferences.set({
        key: "syncVideoGuide",
        value: event.detail.checked ? "1" : "0",
      });
      this.globalContext.syncVideoGuide = event.detail.checked;
    },

    async countCache(path: string) {
      try {
        if (path && !path.endsWith("/")) {
          path = path + '/';
        }
        // Step 1: Read the directory
        const result = await Filesystem.readdir({
            path: path,
            directory: Directory.Data,
        });

        for (const file of result.files) {
            // Check if it's a directory
            const isDirectory = await this.checkIfDirectory(path + file.name);

            if (isDirectory) {
                // Recursively call the function for subdirectory
                await this.countCache(path + file.name);
            } else {
                // Exclude the playlists directory and playlist.json files
                if (file.name === "playlist.json") {
                    continue; // Skip this file
                }

                this.bufferSize += await this.getFileSize(path + file.name);
            }
        }
      } catch (error) {
        console.error("Error counting cache:", error);
      }
    },

    // Рекурсивная очистка кэша в Directory.Data. Удаляются все файлы, кроме playlist.json.
    // Включает: релизы (видео, музыка, гайды, картинки), офлайн-видео образования (Education_Offline) и прочие данные приложения.
    async clearCacheExceptPlaylists(path: string) {
      this.removingCache = true;

      try {
        if (path && !path.endsWith("/")) {
          path = path + '/';
        }
        const result = await Filesystem.readdir({
            path: path,
            directory: Directory.Data,
        });

        for (const file of result.files) {
            const isDirectory = await this.checkIfDirectory(path + file.name);

            if (isDirectory) {
                await this.clearCacheExceptPlaylists(path + file.name);
            } else {
                if (file.name === "playlist.json") {
                    continue;
                }

                // Удаляем файл (в т.ч. офлайн-видео образования, кэш релизов и т.д.)
                await Filesystem.deleteFile({
                    path: path + file.name,
                    directory: Directory.Data,
                });

                console.log(`Deleted: ${file.name}`);
            }
        }
      } catch (error) {
        console.error("Error clearing cache:", error);
      } finally {
        setTimeout(() => {
          this.removingCache = false;
          
          this.bufferSize = 0;
          this.deviceDiskTotal = 0;
          this.deviceDiskFree = 0;
          this.countCache("/");

          if (this.isMobile) {
            Device.getInfo().then((deviceInfo) => {
              this.deviceDiskTotal = deviceInfo.realDiskTotal || 0;
              this.deviceDiskFree = deviceInfo.realDiskFree || 0;
            });
          }

        }, 2000);
      }
    },

    // Helper function to check if the path is a directory
    async checkIfDirectory(path: string) {
        try {
            const result = await Filesystem.stat({
                path: path,
                directory: Directory.Data,
            });
            return result.type === 'directory'; // Will return true if it's a directory
        } catch {
            return false; // If stat fails, it's not a directory
        }
    },

    async getFileSize(path: string) {
        try {
            const result = await Filesystem.stat({
                path: path,
                directory: Directory.Data,
            });
            
            if (result.type !== 'directory') {
              return result.size;
            }
        } catch {
            return 0;
        }

        return 0;
    }

  },
});
</script>

<style scoped>
h1 {
  letter-spacing: 0.08em;
}

ion-item {
  border: 1px solid #434343;
  border-radius: 10px;
  margin-bottom: 1em;
  opacity: 0.9;
  --min-height: 80px;
  color: white;
}

ion-toggle {
  --background-checked: var(--ion-color-orange);
  --background: #5a5a5a;
  --handle-background-checked: white;
}

ion-icon {
  color: white;
}
</style>